import { useCallback, useEffect, useMemo, useState } from "react";

import Alabanza from "./Alabanza";
import AvisoFlotante from "../../shared/components/AvisoFlotante";
import { eliminar, guardarVarias, listar, programaDeReunion, rpc } from "../../services/datos";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import Esqueleto from "../../shared/components/Esqueleto";
const vacio = { titulo_predica: "", predicador: "", version_biblica: "", versiculos: "", notas: "" };

function nombreServicio(servicio) {
  const fecha = servicio.fecha ? new Date(`${servicio.fecha}T00:00:00`).toLocaleDateString("es-CO") : "Sin fecha";
  return `${fecha} · ${servicio.tipos_servicio?.nombre || servicio.titulo || "Servicio"}`;
}

function ProgramacionServicio({ contexto }) {
  const [servicios, setServicios] = useState([]);
  const [servicioId, setServicioId] = useState("");
  const [programacion, setProgramacion] = useState(null);
  const [formulario, setFormulario] = useState(vacio);
  const [historial, setHistorial] = useState([]);
  const [editandoPredica, setEditandoPredica] = useState(true);
  const [pestana, setPestana] = useState("programacion");
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState("");
  const cerrarAviso = useCallback(() => setMensaje(""), []);
  const input = "campo";
  const label = "etiqueta";
  const canciones = useMemo(
    () => [...(programacion?.canciones_programacion || [])].sort((a, b) => a.orden - b.orden),
    [programacion?.canciones_programacion]
  );

  useEffect(() => {
    // Primero se archiva el programa de las reuniones que ya pasaron (queda en el historial).
    rpc("archivar_programaciones_vencidas").catch(() => {})
      .then(() => listar("servicios", { select: "*, tipos_servicio(nombre, dirigido_a)", orden: "fecha" }))
      .then((data) => {
      const lista = Array.isArray(data) ? data : [];
      setServicios(lista);
      // Se abre la reunión elegida en Calendario o, si no, la próxima reunión.
      const hoy = new Date().toLocaleDateString("en-CA");
      const proxima = [...lista].filter((s) => s.fecha >= hoy).sort((a, b) => a.fecha.localeCompare(b.fecha))[0];
      const inicial = contexto?.servicioId || proxima?.id || lista[0]?.id;
      if (inicial) setServicioId(String(inicial));
    }).catch(() => setMensaje("No se pudieron cargar los servicios.")).finally(() => setCargando(false));
  }, [contexto?.servicioId]);

  const cargarHistorial = () => {
    listar("historial_programaciones_servicio", { orden: "fecha_servicio", descendente: true })
      .then((data) => setHistorial(Array.isArray(data) ? data : []))
      .catch(() => setMensaje("No se pudo cargar el historial de programación."));
  };

  useEffect(() => { cargarHistorial(); }, []);

  useEffect(() => {
    if (!servicioId) return;
    setCargando(true);
    programaDeReunion(servicioId).then((data) => {
      setProgramacion(data || null);
      setFormulario(data ? {
        titulo_predica: data.titulo_predica || "", predicador: data.predicador || "",
        version_biblica: data.version_biblica || "", versiculos: data.versiculos || "", notas: data.notas || "",
      } : vacio);
      setEditandoPredica(!data);
    }).catch(() => setMensaje("No se pudo cargar esta programación.")).finally(() => setCargando(false));
  }, [servicioId]);

  const guardarPredica = async () => {
    // Una por reunión: si ya existe, se actualiza.
    const [data] = await guardarVarias("programaciones_servicio", [{ servicio_id: Number(servicioId), ...formulario }], "servicio_id");
    setProgramacion({ ...data, canciones_programacion: programacion?.canciones_programacion || [] });
    setEditandoPredica(false);
    return data;
  };

  const guardar = async () => {
    try { await guardarPredica(); setMensaje("Predicación guardada correctamente."); }
    catch (error) { setMensaje(error.message); }
  };

  // La alabanza necesita una programación guardada; si aún no existe, se crea con lo escrito.
  const asegurarProgramacion = async () => (programacion?.id ? programacion : guardarPredica());

  const actualizarCanciones = (lista) =>
    setProgramacion((actual) => ({ ...actual, canciones_programacion: lista }));

  const eliminarProgramacion = async () => {
    if (!programacion || !(await confirmar("¿Eliminar la prédica y todas sus canciones de este servicio?", { aceptar: "Eliminar" }))) return;
    try {
      await eliminar("programaciones_servicio", programacion.id);
      setProgramacion(null);
      setFormulario(vacio);
      setEditandoPredica(true);
      setMensaje("Programación eliminada.");
    } catch (error) { setMensaje(error.message); }
  };

  return <div className="p-6 max-w-6xl">
    <h2 className="titulo-pagina">Programa</h2>
    <p className="text-gray-400 text-sm mt-1 mb-6">Prepara la prédica y las canciones de cada reunión. Todos los servidores de esa reunión lo ven desde su cuenta.</p>
    <div className="flex flex-wrap gap-2 mb-6">
      {[['programacion', 'Programa'], ['historial', 'Historial']].map(([id, etiqueta]) => <button key={id} onClick={() => setPestana(id)} className={`pestana ${pestana === id ? "pestana-activa" : ""}`}>{etiqueta}</button>)}
    </div>
    {pestana === "programacion" && <>
    <section className="panel p-5 mb-6">
      <label className={label}>Servicio</label>
      <Selector value={servicioId} onChange={(e) => setServicioId(e.target.value)} className={input}>
        {!servicios.length && <option value="">No hay servicios creados</option>}
        {servicios.map((servicio) => <option key={servicio.id} value={servicio.id}>{nombreServicio(servicio)}</option>)}
      </Selector>
    </section>
    {cargando ? <Esqueleto texto="Cargando programación…" /> : servicioId && <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <section className="panel p-5">
        <h3 className="text-xl font-black uppercase tracking-tight text-white mb-5">Predicación</h3>
        {(!programacion || editandoPredica) && <div className="space-y-4">
          <div><label className={label}>Título de la prédica</label><input className={input} value={formulario.titulo_predica} onChange={(e) => setFormulario({ ...formulario, titulo_predica: e.target.value })} /></div>
          <div><label className={label}>Predicador(a)</label><input className={input} value={formulario.predicador} onChange={(e) => setFormulario({ ...formulario, predicador: e.target.value })} /></div>
          <div><label className={label}>Versión bíblica</label><input className={input} value={formulario.version_biblica} onChange={(e) => setFormulario({ ...formulario, version_biblica: e.target.value })} placeholder="Ej: Reina-Valera 1960" /></div>
          <div><label className={label}>Versículos</label><textarea className={input} rows={4} value={formulario.versiculos} onChange={(e) => setFormulario({ ...formulario, versiculos: e.target.value })} placeholder="Ej: Juan 3:16; Romanos 8:28" /></div>
          <div><label className={label}>Notas adicionales</label><textarea className={input} rows={4} value={formulario.notas} onChange={(e) => setFormulario({ ...formulario, notas: e.target.value })} /></div>
          <button onClick={guardar} className="w-full btn-primario">Guardar prédica</button>
        </div>}
        {programacion && <article className="mt-6 bg-oscuro border border-dorado rounded-xl p-4">
          <p className="text-white font-bold text-xs uppercase tracking-wide">Prédica guardada</p>
          <p className="text-white font-semibold mt-2">{programacion.titulo_predica || "Sin título"}</p>
          {programacion.predicador && <p className="text-gray-400 text-sm mt-1">Predica: {programacion.predicador}</p>}
          {programacion.version_biblica && <p className="text-gray-400 text-sm mt-1">Versión: {programacion.version_biblica}</p>}
          {programacion.versiculos && <p className="text-white text-sm mt-3 whitespace-pre-line">{programacion.versiculos}</p>}
          {programacion.notas && <p className="text-gray-400 text-sm mt-3 whitespace-pre-line">Notas: {programacion.notas}</p>}
          <div className="flex gap-3 mt-4">
            <button onClick={() => setEditandoPredica(true)} className="flex-1 btn-fantasma btn-sm">Editar</button>
            <button onClick={eliminarProgramacion} className="flex-1 btn-peligro btn-sm">Eliminar</button>
          </div>
        </article>}
      </section>
      <Alabanza key={servicioId} canciones={canciones} asegurarProgramacion={asegurarProgramacion}
        onCambio={actualizarCanciones} avisar={setMensaje} />
    </div>}
    </>}
    {pestana === "historial" && <section className="panel p-5">
      <h3 className="text-xl font-black uppercase tracking-tight text-white">Historial de programaciones</h3>
      <p className="text-gray-400 text-sm mt-1 mb-4">Se conserva únicamente el último servicio realizado de cada tipo.</p>
      {historial.length === 0 ? <p className="text-gray-400 text-sm">Aún no hay programaciones históricas.</p> : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{historial.map((registro) => <article key={registro.id} className="panel-interior p-4"><p className="text-white font-bold text-xs">{registro.tipo_servicio}</p><p className="text-white font-semibold mt-2">{registro.titulo_predica || "Sin título de prédica"}</p><p className="text-gray-400 text-xs mt-1">{registro.fecha_servicio ? new Date(`${registro.fecha_servicio}T00:00:00`).toLocaleDateString("es-CO") : "Sin fecha"}{registro.predicador ? ` · ${registro.predicador}` : ""}</p>{registro.versiculos && <p className="text-gray-300 text-sm mt-3 whitespace-pre-line">{registro.versiculos}</p>}<p className="text-gray-400 text-xs mt-3">{(registro.canciones || []).length} canción(es)</p></article>)}</div>}
    </section>}
    <AvisoFlotante mensaje={mensaje} onCerrar={cerrarAviso} />
  </div>;
}

export default ProgramacionServicio;
